/**
 * Pure helper to build job detail URL with returnTo for the static SRP shell.
 * No hooks or context; used so static shell can render job links without SearchProvider.
 */
import { generateJobUrl, toRelativePath } from './generate-url';
import type { Locale } from './generate-url';
import { generateUrlFromIndexUiState } from '../algolia/url-handling';
import { BASE_URL, DEV_URL } from '../constants/url-constants';

export interface StaticJobUrlParams {
  hit: {
    objectID: string;
    normalisedTitle?: string;
    title?: string;
    companyId?: string;
    location?: string | string[];
    remoteness?: string;
  };
  locale: Locale;
  /** Current search path for returnTo (e.g. /en/jobs). Must match server. */
  currentPathname: string;
  query: string;
  filters: Record<string, string[]> | string;
  page: number;
  indexName: string;
  baseUrl?: string;
}

/**
 * Returns relative job URL with returnTo query param for the static SRP.
 */
export function buildStaticJobUrl(params: StaticJobUrlParams): string {
  const baseUrl = params.baseUrl ?? (typeof import.meta !== 'undefined' && (import.meta as any).env?.DEV ? DEV_URL : BASE_URL);
  const locationStr = Array.isArray(params.hit.location) && params.hit.location.length > 0
    ? String(params.hit.location[0] ?? '')
    : String(params.hit.location ?? '');

  const absolute = generateJobUrl({
    baseUrl,
    jobId: params.hit.objectID,
    locale: params.locale,
    title: params.hit.normalisedTitle ?? params.hit.title,
    company: params.hit.companyId ?? '',
    location: locationStr,
    remoteness: String(params.hit.remoteness ?? ''),
  });

  let relative = toRelativePath(absolute);

  const filtersObj = typeof params.filters === 'string'
    ? (() => {
        try {
          return (JSON.parse(params.filters) as Record<string, string[]>) || {};
        } catch {
          return {};
        }
      })()
    : params.filters;

  const currentSearchPath = generateUrlFromIndexUiState(
    {
      query: params.query,
      refinementList: filtersObj,
      page: params.page,
    },
    params.indexName,
    params.currentPathname
  );

  if (currentSearchPath) {
    const separator = relative.includes('?') ? '&' : '?';
    relative += `${separator}returnTo=${encodeURIComponent(currentSearchPath)}`;
  }

  return relative;
}
